import ReactECharts from 'echarts-for-react';
import { useMemo } from 'react';
import type { CustomSeriesRenderItemAPI, CustomSeriesRenderItemParams } from 'echarts';
import { t } from 'utilities/TranslationUtils';
import type { FreeKickZoneDto, MeasurementMarkerDto } from 'types/models/SessionsPageData';
import { buildZoneStats, getFreeKickZoneIdForLayout } from './playerPerformanceUtils';
import { buildFreeKickZoneOption, COMPACT_Y_MIN, polygonVisualCentre, visibleZonePolygon } from './freeKickZonePitchOptions';
import './FreeKickZonePitch.scss';

interface Props {
  /** Zones of every layout; the pitch draws the ones matching `selectedLayoutId`. */
  zones: FreeKickZoneDto[];
  measurements: MeasurementMarkerDto[];
  selectedLayoutId: string;
  selectedZones: Set<number>;
  onToggleZone: (zoneId: number) => void;
}

/**
 * Renders a zone polygon with accuracy % + kick count labels.
 *
 * The label anchor is the middle of the annular sector: mid-band radius on the column-centre
 * angle. That keeps it inside the sector regardless of aspect ratio or zoom level, centred in
 * thin and thick bands alike, and makes symmetric left/right zones perfectly mirror-symmetric.
 *
 * Available space is measured in pixels from the actual arc boundaries, not from
 * the bounding box, so text is never sized to overflow into neighbouring zones.
 */
function renderZonePolygon(
  polygon: [number, number][],
  { innerRadius: innerR, outerRadius: outerR, centreAngle: colAngle, halfSpan }: { innerRadius: number; outerRadius: number; centreAngle: number; halfSpan: number },
  stat: { kickCount: number; onTargetPct: number | null },
  selectionState: { selectionActive: boolean; isSelected: boolean },
  itemStyle: { color?: string; opacity?: number; borderColor?: string; borderWidth?: number },
  yMin: number,
) {
  // Pre-compute zone-specific geometry (constant per zone, safe to capture in closure)
  // Label sits at the middle of the band (not hugging the outer arc) so it reads as centred in every layout.
  const labelR = (innerR + outerR) / 2;
  // Soccer-to-chart: chartZ = -soccerZ, chartX = soccerX
  const midBandChartZ = -Math.sin(colAngle) * labelR;   // = -soccerZ
  const midBandChartX =  Math.cos(colAngle) * labelR;   // = soccerX
  const visible = visibleZonePolygon(polygon, yMin);
  // The label is centred on the middle of the part of the zone that is actually on screen (its deepest interior point):
  // a mid-radius point hugs the edge of a zone cut off by the view, and a centroid falls outside a curved band.
  const [anchorChartZ, anchorChartX] = polygonVisualCentre(visible) ?? [midBandChartZ, midBandChartX];

  return (params: CustomSeriesRenderItemParams, api: CustomSeriesRenderItemAPI) => {
    const points = polygon.map(([z, x]) => api.coord([z, x]));
    const { kickCount, onTargetPct } = stat;
    const isHovered = (params as any).state === 'emphasis';
    const showLabels = !selectionState.selectionActive || selectionState.isSelected || isHovered;
    const labelOpacity = showLabels ? 1 : 0.34;

    // Label anchor in pixel space
    const [labelPx, labelPy] = api.coord([anchorChartZ, anchorChartX]);

    // Available height: pixel distance between inner and outer arc at the column angle
    const [, innerPy] = api.coord([-Math.sin(colAngle) * innerR, Math.cos(colAngle) * innerR]);
    const [, outerPy] = api.coord([-Math.sin(colAngle) * outerR, Math.cos(colAngle) * outerR]);
    const arcHeightPx = Math.abs(outerPy - innerPy);

    // Available width: pixel distance across the column at labelR (span supplied per zone by the BFF)
    const [leftPx]  = api.coord([-Math.sin(colAngle - halfSpan) * labelR, Math.cos(colAngle - halfSpan) * labelR]);
    const [rightPx] = api.coord([-Math.sin(colAngle + halfSpan) * labelR, Math.cos(colAngle + halfSpan) * labelR]);
    const arcWidthPx = Math.abs(rightPx - leftPx);

    // The visible part may be smaller than the full arc box: never size the text beyond it.
    const visiblePx = visible.map(([z, x]) => api.coord([z, x]));
    const visibleW = visiblePx.length ? Math.max(...visiblePx.map((p) => p[0])) - Math.min(...visiblePx.map((p) => p[0])) : arcWidthPx;
    const visibleH = visiblePx.length ? Math.max(...visiblePx.map((p) => p[1])) - Math.min(...visiblePx.map((p) => p[1])) : arcHeightPx;
    const availableWidth  = Math.max(8, Math.min(arcWidthPx, visibleW) - 8);
    const availableHeight = Math.max(8, Math.min(arcHeightPx, visibleH) - 10);

    const percentageText = kickCount === 0 ? '-' : `${Math.round(onTargetPct ?? 0)}%`;
    const kicksText       = kickCount === 0 ? '-' : `${kickCount} ${kickCount === 1 ? 'kick' : 'kicks'}`;

    const basePctFont = 22;
    const baseSubFont = 11;
    const baseGap     = 8;

    const estimateTextWidth = (text: string, fontSize: number) => Math.max(1, text.length) * fontSize * 0.64;
    const widthScale  = Math.min(1, availableWidth  / Math.max(estimateTextWidth(percentageText, basePctFont), estimateTextWidth(kicksText, baseSubFont)));
    const heightScale = Math.min(1, availableHeight / (basePctFont + baseGap + baseSubFont));
    const scale = Math.max(0.28, Math.min(widthScale, heightScale));

    const pctFontSize = Math.max(9,  Math.round(basePctFont * scale));
    const subFontSize = Math.max(7,  Math.round(baseSubFont * scale));
    const gap         = Math.max(3,  Math.round(baseGap     * scale));

    const blockHeight  = pctFontSize + gap + subFontSize;
    const percentageY  = labelPy - blockHeight / 2 + pctFontSize / 2;
    const subtitleY    = percentageY + pctFontSize / 2 + gap + subFontSize / 2;

    // Optical correction for heavy glyphs like '%'
    const cx = labelPx - 1;

    const baseOpacity    = itemStyle.opacity ?? 0.6;
    const boostedOpacity = Math.min(0.95, baseOpacity + 0.16);

    return {
      type: 'group' as const,
      children: [
        {
          type: 'polygon' as const,
          shape: { points },
          style: {
            fill: itemStyle.color,
            opacity: showLabels ? boostedOpacity : baseOpacity,
            stroke: itemStyle.borderColor,
            lineWidth: itemStyle.borderWidth,
          },
        },
        // Percentage drop shadow
        {
          type: 'text' as const,
          style: { text: percentageText, x: cx + 1, y: percentageY + 2, textAlign: 'center', textVerticalAlign: 'middle', font: `700 ${pctFontSize}px Inter, sans-serif`, fill: 'rgba(0,0,0,0.5)', opacity: labelOpacity },
          silent: true,
        },
        // Percentage text
        {
          type: 'text' as const,
          style: { text: percentageText, x: cx, y: percentageY, textAlign: 'center', textVerticalAlign: 'middle', font: `700 ${pctFontSize}px Inter, sans-serif`, fill: '#ffffff', opacity: labelOpacity },
          silent: true,
        },
        // Kicks drop shadow
        {
          type: 'text' as const,
          style: { text: kicksText, x: cx + 1, y: subtitleY + 2, textAlign: 'center', textVerticalAlign: 'middle', font: `600 ${subFontSize}px Inter, sans-serif`, fill: 'rgba(0,0,0,0.5)', opacity: labelOpacity },
          silent: true,
        },
        // Kicks text
        {
          type: 'text' as const,
          style: { text: kicksText, x: cx, y: subtitleY, textAlign: 'center', textVerticalAlign: 'middle', font: `600 ${subFontSize}px Inter, sans-serif`, fill: '#ffffff', opacity: labelOpacity },
          silent: true,
        },
      ],
    };
  };
}

export default function FreeKickZonePitch({ zones, measurements, selectedLayoutId, selectedZones, onToggleZone }: Props) {
  const yMin = COMPACT_Y_MIN;

  const layoutZones = useMemo(() => zones.filter((z) => z.layoutId === selectedLayoutId), [zones, selectedLayoutId]);
  const stats = useMemo(
    () => buildZoneStats(measurements, (m) => getFreeKickZoneIdForLayout(m, selectedLayoutId)),
    [measurements, selectedLayoutId],
  );
  const option = useMemo(
    () => buildFreeKickZoneOption({ zones: layoutZones, stats, kicks: measurements, selected: selectedZones, yMin, layoutId: selectedLayoutId }),
    [layoutZones, stats, measurements, selectedZones, yMin, selectedLayoutId],
  );

  const seriesWithRender = useMemo(() => (option.series as any[]).map((s) => {
    if (s.type !== 'custom') return s;
    const zoneId = typeof s.id === 'string' && s.id.startsWith('fk-zone-')
      ? Number(s.id.replace('fk-zone-', ''))
      : null;
    if (zoneId == null || Number.isNaN(zoneId)) return s;
    const zoneData = (s.data?.[0] ?? {}) as { kickCount?: number; onTargetPct?: number | null };
    const stat = { kickCount: zoneData.kickCount ?? 0, onTargetPct: zoneData.onTargetPct ?? null };
    const selectionState = { selectionActive: selectedZones.size > 0, isSelected: selectedZones.has(zoneId) };
    return { ...s, renderItem: renderZonePolygon(s.polygon ?? [], s.geometry, stat, selectionState, s.itemStyle ?? {}, yMin) };
  }), [option, selectedZones]);

  const onEvents = useMemo(() => ({
    click: (p: any) => {
      const id = typeof p.seriesId === 'string' && p.seriesId.startsWith('fk-zone-')
        ? Number(p.seriesId.replace('fk-zone-', '')) : null;
      if (id == null || Number.isNaN(id)) return;
      // Zones with no kicks are not selectable — tapping them is a no-op.
      if (!stats.get(id)?.kickCount) return;
      onToggleZone(id);
    },
  }), [onToggleZone, stats]);

  return (
    <div className='pp-section fk-zone-pitch' data-test='fk-zone-pitch'>
      <div className='fk-zone-pitch__chart fk-zone-pitch__chart--compact'>
        <ReactECharts
          option={{ ...option, series: seriesWithRender }}
          onEvents={onEvents}
          style={{ width: '100%', height: '100%' }}
          notMerge
        />
      </div>
    </div>
  );
}
